const COORD_CELL = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;

export function stripStopCellWrappingQuotes(value) {
  let v = String(value || '').trim();
  for (;;) {
    if (v.length >= 2 && v.startsWith('"') && v.endsWith('"')) {
      v = v.slice(1, -1).trim();
      continue;
    }
    if (v.length >= 2 && v.startsWith("'") && v.endsWith("'")) {
      v = v.slice(1, -1).trim();
      continue;
    }
    break;
  }
  return v;
}

export function normalizeRoutePlannerHeader(header) {
  return String(header || '')
    .trim()
    .replace(/\s*\[(required|optional)\]\s*$/i, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

/** Same shape as RoutePlanner CSV export (vue-xls-csv-parser may yield Date for departure). */
export function formatRoutePlannerDepartureValue(value) {
  if (value == null) {
    return '';
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const h = value.getHours();
    const min = String(value.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${h}:${min}`;
  }
  return String(value).trim();
}

export function normalizeRoutePlannerRow(raw) {
  const out = {};
  Object.entries(raw || {}).forEach(([key, value]) => {
    const normalizedKey = normalizeRoutePlannerHeader(key);
    if (normalizedKey === 'departure') {
      out[normalizedKey] = formatRoutePlannerDepartureValue(value);
    } else if (normalizedKey === 'route name' && value instanceof Date) {
      out[normalizedKey] = '';
    } else {
      out[normalizedKey] = value == null ? '' : String(value).trim();
    }
  });
  return out;
}

function isRowEmpty(row) {
  return !Object.values(row || {}).some((v) => String(v || '').trim() !== '');
}

export function locationKeys() {
  const keys = ['start'];
  for (let n = 1; n <= 49; n++) {
    keys.push(`location${n}`);
  }
  return keys;
}

export function collectStopsFromRow(row) {
  const stops = [];
  locationKeys().forEach((key) => {
    const v = stripStopCellWrappingQuotes(row[key]);
    if (!v) {
      return;
    }
    const m = v.match(COORD_CELL);
    if (m) {
      stops.push({ text: v, lat: m[1], lon: m[2] });
    } else {
      stops.push({ text: v, lat: '', lon: '' });
    }
  });
  return stops;
}

/** Returns missing required column keys (start only; departure defaults on import). */
export function validateRoutePlannerImportRow(row) {
  const missing = [];
  if (!String(row.start || '').trim()) {
    missing.push('start');
  }
  return missing;
}

/** One non-empty CSV data row → one route import entry. */
export function prepareImportRows(rawRows) {
  const normalized = rawRows.map((r) => normalizeRoutePlannerRow(r));
  const entries = [];
  normalized.forEach((row, dataIndex) => {
    if (isRowEmpty(row)) {
      return;
    }
    entries.push({
      row,
      blockStartRowIndex: dataIndex + 2,
    });
  });
  return entries;
}

/** @deprecated Use prepareImportRows — one row per route. */
export function prepareImportBlocks(rawRows) {
  return prepareImportRows(rawRows);
}

export function csvHasRoutePlannerHeaders(rawRows) {
  if (!rawRows.length) {
    return false;
  }
  const headers = Object.keys(rawRows[0] || {}).map((h) =>
    normalizeRoutePlannerHeader(h),
  );
  return headers.includes('start');
}

/** Keep first CSV header per normalized column name (avoids duplicate cell-error). */
export function dedupeRoutePlannerDisplayHeaders(headers) {
  const seen = new Set();
  const out = [];
  (headers || []).forEach((h) => {
    if (!h) {
      return;
    }
    const n = normalizeRoutePlannerHeader(h);
    if (seen.has(n)) {
      return;
    }
    seen.add(n);
    out.push(h);
  });
  return out;
}

/** Column order for UI: route name first, then original file order. */
export function orderRoutePlannerDisplayHeaders(headers) {
  const list = Array.isArray(headers) ? headers.filter(Boolean) : [];
  if (!list.length) {
    return [];
  }
  let routeNameHeader = null;
  const rest = [];
  list.forEach((h) => {
    if (normalizeRoutePlannerHeader(h) === 'route name') {
      if (!routeNameHeader) {
        routeNameHeader = h;
      } else {
        rest.push(h);
      }
    } else {
      rest.push(h);
    }
  });
  if (routeNameHeader) {
    return [routeNameHeader, ...rest];
  }
  return list;
}

export function routePlannerFileJsonToRawRows(fileJson, isEmptyRow) {
  if (!fileJson?.length) {
    return [];
  }
  const columns = {};
  fileJson.forEach(({ name, data }) => {
    columns[name] = data;
  });
  const rowCount = fileJson[0].data.length;
  const rows = [];
  const isEmpty =
    isEmptyRow ||
    ((values) => !values.some((v) => String(v ?? '').trim() !== ''));
  for (let i = 0; i < rowCount; i++) {
    const row = {};
    Object.keys(columns).forEach((name) => {
      row[name] = columns[name][i];
    });
    if (!isEmpty(Object.values(row))) {
      rows.push(row);
    }
  }
  return rows;
}

export function displayCellValue(raw, header) {
  const key = Object.keys(raw || {}).find(
    (k) => normalizeRoutePlannerHeader(k) === normalizeRoutePlannerHeader(header),
  );
  if (key == null) {
    return '';
  }
  const value = raw[key];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    if (normalizeRoutePlannerHeader(header) === 'departure') {
      return formatRoutePlannerDepartureValue(value);
    }
    return String(value);
  }
  return value == null ? '' : String(value);
}

export function csvHasLegacyWideHeaders(rawRows) {
  if (!rawRows.length) {
    return false;
  }
  const headers = Object.keys(rawRows[0] || {}).map((h) =>
    normalizeRoutePlannerHeader(h),
  );
  return (
    (headers.includes('route name') || headers.includes('stop 1')) &&
    !headers.includes('departure')
  );
}
